import { useEffect, useState } from "react";
import {
  createTournament,
  defaultConfig,
  parseCsv,
  possiblePoolCounts,
  type Court,
  type Draft,
  type Mode,
  type Player,
  type PointsTable,
  type Team,
  type Tournament,
} from "@spikeeasy/tournament";
import { api } from "./api";
import { Field, modeLabels, Notice, teamLabel } from "./ui";
import { DrawView } from "./DrawView";
import { TeamBuilder } from "./TeamBuilder";

export function PointsEditor({ done }: { done: () => void }) {
  const [tables, setTables] = useState<PointsTable[]>([]);
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const [awards, setAwards] = useState("");
  const [isDefault, setDefault] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    api<{ tables: PointsTable[] }>("/points-tables")
      .then((r) => setTables(r.tables))
      .catch((e) => setError(e.message));
  }, []);
  return (
    <form
      className="editor stack"
      onSubmit={async (e) => {
        e.preventDefault();
        setError("");
        try {
          const rows = parseCsv(awards);
          if (
            rows.some((row) => row.length !== 2 || row.some((value) => !value))
          )
            throw new Error(
              "Each row needs a place and points, separated by a comma.",
            );
          const table = {
            id: selected || crypto.randomUUID(),
            name,
            awards: rows.map((row) => ({
              place: Number(row[0]),
              points: Number(row[1]),
            })),
          };
          await api("/points-tables", { table, isDefault });
          setTables((previous) => [
            ...previous.filter((t) => t.id !== table.id),
            table,
          ]);
          setSelected(table.id);
          setMessage(
            "Points table saved. Existing tournament awards stay unchanged.",
          );
        } catch (e) {
          setError((e as Error).message);
        }
      }}
    >
      <h1>Points tables</h1>
      <p>
        Each player receives the full award for their team’s place. Places
        without an entry receive zero points.
      </p>
      <Field label="Table">
        <select
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value);
            const t = tables.find((t) => t.id === e.target.value);
            setName(t?.name ?? "");
            setAwards(
              t?.awards.map((a) => `${a.place},${a.points}`).join("\n") ?? "",
            );
            setMessage("");
          }}
        >
          <option value="">New table</option>
          {tables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Table name">
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field
        label="Awards"
        hint="One row per place: place,points. Use the shared finishing place for tied exits, such as 5 for quarterfinal exits."
      >
        <textarea
          required
          rows={8}
          value={awards}
          onChange={(e) => setAwards(e.target.value)}
        />
      </Field>
      <label className="check">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setDefault(e.target.checked)}
        />
        Use for new tournaments by default
      </label>
      {error && <Notice error>{error}</Notice>}
      {message && <Notice>{message}</Notice>}
      <div className="actions">
        <button className="primary">Save table</button>
        <button type="button" className="text-button" onClick={done}>
          Back to tournaments
        </button>
      </div>
    </form>
  );
}

export function Setup({
  existing,
  saved,
  cancel,
}: {
  existing?: Tournament;
  saved: (t: Tournament) => void;
  cancel: () => void;
}) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [tables, setTables] = useState<PointsTable[]>([]);
  const [name, setName] = useState(existing?.name ?? "");
  const [location, setLocation] = useState(
    existing?.location ?? "Ocean Beach, San Francisco",
  );
  const [timezone, setTimezone] = useState(
    existing?.timezone ?? "America/Los_Angeles",
  );
  const [tableId, setTableId] = useState(existing?.config.pointsTable.id ?? "");
  const [config, setConfig] = useState(
    existing?.config ??
      defaultConfig({ id: "unselected", name: "", awards: [] }),
  );
  const [teams, setTeams] = useState<Team[]>(existing?.teams ?? []);
  const [courts, setCourts] = useState<Court[]>(
    existing?.courts ?? [
      {
        id: "c1",
        name: "Court 1",
        netTeamId: null,
        windows: [
          {
            date: new Date().toLocaleDateString("en-CA"),
            opens: "09:00",
            closes: "18:00",
          },
        ],
      },
    ],
  );
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState<Tournament | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setLoadError("");
    Promise.all([
      api<{ players: Player[] }>("/players"),
      api<{ tables: PointsTable[]; defaultId: string | null }>(
        "/points-tables",
      ),
    ])
      .then(([p, t]) => {
        if (!current) return;
        setPlayers(p.players);
        setTeams((teams) =>
          teams.map((team) => ({
            ...team,
            players: [
              p.players.find((p) => p.id === team.players[0].id) ??
                team.players[0],
              p.players.find((p) => p.id === team.players[1].id) ??
                team.players[1],
            ],
          })),
        );
        setTables(t.tables);
        if (!existing && t.defaultId) setTableId(t.defaultId);
      })
      .catch((e: Error) => {
        if (current) setLoadError(e.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [existing, reload]);
  const draft = (): Draft => ({
    name,
    location,
    timezone,
    teams,
    courts,
    config: {
      ...config,
      pointsTable: tables.find((t) => t.id === tableId) ?? config.pointsTable,
    },
  });
  const next = () => {
    setError("");
    try {
      if (step === 0 && (!name.trim() || !tableId))
        throw new Error(
          "Enter a name and choose a points table. Create a table from the tournament list if needed.",
        );
      if (step === 1) {
        if (teams.length < 8 || teams.length > 50)
          throw new Error("Add between 8 and 50 teams.");
        if (!possiblePoolCounts(teams.length).includes(config.poolCount))
          setConfig({
            ...config,
            poolCount: possiblePoolCounts(teams.length)[0] ?? 2,
          });
      }
      if (step === 2)
        setPreview(
          createTournament(
            existing?.id ?? "preview",
            draft(),
            new Date().toISOString(),
          ),
        );
      setStep(step + 1);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const updateCourt = (i: number, value: Court) =>
    setCourts(courts.map((court, j) => (j === i ? value : court)));
  return (
    <section className="setup stack">
      <div className="section-heading">
        <h1>{existing ? "Edit the draft" : "Build the draw"}</h1>
        <button className="text-button" onClick={cancel}>
          Cancel
        </button>
      </div>
      <ol className="steps" aria-label="Setup progress">
        {["Tournament", "Teams", "Rules & courts", "Review"].map((label, i) => (
          <li key={label} aria-current={i === step ? "step" : undefined}>
            {i + 1}. {label}
          </li>
        ))}
      </ol>
      {error && <Notice error>{error}</Notice>}
      {step === 0 && (
        <div className="editor stack">
          <Field label="Tournament name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={120}
            />
          </Field>
          <Field label="Location">
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
            />
          </Field>
          <Field label="Timezone">
            <input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            />
          </Field>
          <Field label="Format">
            <select
              value={config.mode}
              onChange={(e) =>
                setConfig({ ...config, mode: e.target.value as Mode })
              }
            >
              {Object.entries(modeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Points table">
            <select
              value={tableId}
              onChange={(e) => setTableId(e.target.value)}
            >
              <option value="">Choose a saved table</option>
              {tables.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <p className="muted">
            The selected table is copied into this tournament when saved.
          </p>
        </div>
      )}
      {step === 1 && (
        <div className="editor stack">
          <TeamBuilder
            teams={teams}
            players={players}
            playerCreated={(player) =>
              setPlayers((previous) => [...previous, player])
            }
            changed={(next) => {
              setTeams(next);
              setCourts((courts) =>
                courts.map((court) => ({
                  ...court,
                  netTeamId: next.some(
                    (team) => team.id === court.netTeamId && team.bringsNet,
                  )
                    ? court.netTeamId
                    : null,
                })),
              );
            }}
          />
        </div>
      )}
      {step === 2 && (
        <div className="stack">
          <div className="editor stack">
            <h2>Playing rules</h2>
            <div className="form-row">
              <Field label="Points to win a set">
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={config.scoring.target}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      scoring: {
                        ...config.scoring,
                        target: Number(e.target.value),
                      },
                    })
                  }
                />
              </Field>
              <Field label="Sets to win">
                <input
                  type="number"
                  min={1}
                  max={3}
                  value={config.scoring.setsToWin}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      scoring: {
                        ...config.scoring,
                        setsToWin: Number(e.target.value),
                      },
                    })
                  }
                />
              </Field>
            </div>
            <label className="check">
              <input
                type="checkbox"
                checked={config.scoring.winByTwo}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    scoring: { ...config.scoring, winByTwo: e.target.checked },
                  })
                }
              />
              Win by two
            </label>
            <Field label="Hard cap (optional)">
              <input
                type="number"
                min={config.scoring.target}
                value={config.scoring.cap ?? ""}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    scoring: {
                      ...config.scoring,
                      cap: e.target.value ? Number(e.target.value) : null,
                    },
                  })
                }
              />
            </Field>
            {config.mode === "pools" && (
              <Field label="Pools">
                <select
                  value={config.poolCount}
                  onChange={(e) =>
                    setConfig({ ...config, poolCount: Number(e.target.value) })
                  }
                >
                  {possiblePoolCounts(teams.length).map((count) => (
                    <option key={count} value={count}>
                      {count} pools · {Math.floor(teams.length / count)}–
                      {Math.ceil(teams.length / count)} teams each
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {["pools", "swiss", "king"].includes(config.mode) && (
              <Field
                label="Total playoff qualifiers"
                hint={
                  config.mode === "swiss"
                    ? "Use zero to finish with Swiss standings."
                    : "At least two teams qualify."
                }
              >
                <input
                  type="number"
                  min={config.mode === "swiss" ? 0 : 2}
                  max={teams.length}
                  value={config.qualifiers}
                  onChange={(e) =>
                    setConfig({ ...config, qualifiers: Number(e.target.value) })
                  }
                />
              </Field>
            )}
            {config.mode === "swiss" && (
              <Field label="Swiss rounds">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={config.swissRounds}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      swissRounds: Number(e.target.value),
                    })
                  }
                />
              </Field>
            )}
            {config.mode === "king" && (
              <div className="form-row">
                <Field label="Heats per team">
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={config.heatCount}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        heatCount: Number(e.target.value),
                      })
                    }
                  />
                </Field>
                <Field label="Minutes per heat">
                  <input
                    type="number"
                    min={1}
                    max={120}
                    value={config.heatMinutes}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        heatMinutes: Number(e.target.value),
                      })
                    }
                  />
                </Field>
              </div>
            )}
            <label className="check">
              <input
                type="checkbox"
                checked={config.bronze}
                onChange={(e) =>
                  setConfig({ ...config, bronze: e.target.checked })
                }
              />
              Play a third-place match
            </label>
            <Field
              label="Estimated match duration (minutes)"
              hint="Used only for reference schedules, never as a rest requirement."
            >
              <input
                type="number"
                min={1}
                max={180}
                value={config.estimatedMatchMinutes}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    estimatedMatchMinutes: Number(e.target.value),
                  })
                }
              />
            </Field>
          </div>
          <h2>Courts & available days</h2>
          <div className="court-grid">
            {courts.map((court, i) => (
              <section className="editor stack" key={court.id}>
                <Field label="Court name">
                  <input
                    value={court.name}
                    onChange={(e) =>
                      updateCourt(i, { ...court, name: e.target.value })
                    }
                  />
                </Field>
                <Field label="Net provided by">
                  <select
                    value={court.netTeamId ?? ""}
                    onChange={(e) =>
                      updateCourt(i, {
                        ...court,
                        netTeamId: e.target.value || null,
                      })
                    }
                  >
                    <option value="">Organizer / another net</option>
                    {teams
                      .filter((t) => t.bringsNet)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {teamLabel(t)}
                        </option>
                      ))}
                  </select>
                </Field>
                {court.windows.map((window, j) => (
                  <div className="availability" key={j}>
                    <Field label="Date">
                      <input
                        type="date"
                        value={window.date}
                        onChange={(e) =>
                          updateCourt(i, {
                            ...court,
                            windows: court.windows.map((w, k) =>
                              k === j ? { ...w, date: e.target.value } : w,
                            ),
                          })
                        }
                      />
                    </Field>
                    <div className="form-row">
                      <Field label="Open">
                        <input
                          type="time"
                          value={window.opens}
                          onChange={(e) =>
                            updateCourt(i, {
                              ...court,
                              windows: court.windows.map((w, k) =>
                                k === j ? { ...w, opens: e.target.value } : w,
                              ),
                            })
                          }
                        />
                      </Field>
                      <Field label="Close">
                        <input
                          type="time"
                          value={window.closes}
                          onChange={(e) =>
                            updateCourt(i, {
                              ...court,
                              windows: court.windows.map((w, k) =>
                                k === j ? { ...w, closes: e.target.value } : w,
                              ),
                            })
                          }
                        />
                      </Field>
                    </div>
                    {court.windows.length > 1 && (
                      <button
                        className="text-button"
                        onClick={() =>
                          updateCourt(i, {
                            ...court,
                            windows: court.windows.filter((_, k) => k !== j),
                          })
                        }
                      >
                        Remove day
                      </button>
                    )}
                  </div>
                ))}
                <button
                  className="text-button"
                  onClick={() =>
                    updateCourt(i, {
                      ...court,
                      windows: [...court.windows, { ...court.windows.at(-1)! }],
                    })
                  }
                >
                  Add day
                </button>
                {courts.length > 1 && (
                  <button
                    className="text-button"
                    onClick={() => setCourts(courts.filter((_, j) => j !== i))}
                  >
                    Remove court
                  </button>
                )}
              </section>
            ))}
          </div>
          <button
            className="secondary"
            disabled={courts.length >= 16}
            onClick={() =>
              setCourts([
                ...courts,
                {
                  id: crypto.randomUUID(),
                  name: `Court ${courts.length + 1}`,
                  netTeamId: null,
                  windows: structuredClone(courts[0]!.windows),
                },
              ])
            }
          >
            Add court
          </button>
        </div>
      )}
      {step === 3 && preview && (
        <>
          <Notice>
            This saves a private draft. Publish from the organizer view when the
            teams, seeds and courts are ready.
          </Notice>
          <DrawView
            t={preview}
            actor={null}
            onScore={() => {}}
            onManage={() => {}}
            onHeat={() => {}}
          />
        </>
      )}
      <div className="setup-actions">
        {step > 0 && (
          <button className="text-button" onClick={() => setStep(step - 1)}>
            Back
          </button>
        )}
        {step < 3 ? (
          <button
            className="primary"
            disabled={loading || !!loadError}
            onClick={next}
          >
            Continue
          </button>
        ) : (
          <button
            className="primary"
            disabled={busy || loading || !!loadError}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                const result = existing
                  ? await api<{ tournament: Tournament }>(
                      `/tournaments/${existing.id}/commands`,
                      {
                        id: crypto.randomUUID(),
                        version: existing.version,
                        command: { type: "save_draft", draft: draft() },
                      },
                    )
                  : await api<{ tournament: Tournament }>(
                      "/tournaments",
                      draft(),
                    );
                saved(result.tournament);
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Saving…" : "Save draft"}
          </button>
        )}
      </div>
      {loading && <Notice>Loading players and points tables…</Notice>}
      {loadError && (
        <Notice error>
          {loadError}
          <button
            className="text-button"
            onClick={() => setReload((n) => n + 1)}
          >
            Reload players & points tables
          </button>
        </Notice>
      )}
    </section>
  );
}
